import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Linking } from 'react-native';
import { Image } from 'expo-image';
import { SafeAreaView } from 'react-native-safe-area-context';
import CachedImage from '@/components/CachedImage';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
// Phase 1: appointment-reminder rows now show the same camera-record glyph
// used on the lobby/fatal screens. Imported from the shared lobby pill so
// the icon is traced from a single Figma source.
import { RecordVideoGlyph } from '@/components/video/lobby/LobbyControlPill';
import { useNotifications as useNotificationContext } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { SkeletonNotification } from '@/components/Skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabase';

interface UserNotificationStatus {
  id: string;
  notification_id: string;
  is_read: boolean;
  read_at: string | null | undefined;
  notification: {
    id: string;
    title: string;
    subtitle?: string;
    message: string;
    sent_at: string;
    link_url?: string;
    banner_url?: string;
    background_color?: string;
    notification_type: string;
    icon: string;
  };
}

export default function NotificationsModal() {
  const router = useRouter();
  const { t } = useLanguage();
  const notificationContext = useNotificationContext();
  const { session, user, userProfile, sessionReady } = useAuth();
  const [dbNotifications, setDbNotifications] = useState<UserNotificationStatus[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = userProfile?.is_admin === true;

  const loadNotifications = async (isRefreshing = false, forceRefresh = false) => {
    console.log('📱 [Notifications] Starting loadNotifications...');
    try {
      if (!isRefreshing) setLoading(true);

      console.log('📱 [Notifications] Importing NotificationCacheService...');
      const { default: NotificationCacheService } = await import('../services/notificationCacheService');
      
      console.log('📱 [Notifications] Using session from AuthContext:', { 
        hasSession: !!session, 
        hasUser: !!user,
        hasToken: !!session?.access_token,
        sessionReady: sessionReady 
      });

      if (!session?.user || !session?.access_token) {
        console.error('📱 [Notifications] User not authenticated - no session');
        setLoading(false);
        return;
      }

      console.log('📱 [Notifications] Fetching notifications for user:', session.user.id);
      
      // Use cache-first approach for better performance with access token
      const result = await NotificationCacheService.getNotifications(
        session.user.id, 
        forceRefresh, 
        session.access_token
      );

      console.log('📱 [Notifications] Cache service returned:', { 
        count: result.notifications.length, 
        fromCache: result.from_cache 
      });

      // Convert cached notifications to UserNotificationStatus format
      const userNotifications: UserNotificationStatus[] = result.notifications.map(cached => ({
        id: cached.id,
        notification_id: cached.notification_id,
        is_read: cached.is_read,
        read_at: cached.read_at,
        notification: cached.notification
      }));

      setDbNotifications(userNotifications);

      console.log('📱 Loaded', userNotifications.length, 'notifications',
        result.from_cache ? '(from cache)' : '(fresh)');
    } catch (error) {
      console.error('📱 [Notifications] Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionReady && session?.user?.id) {
      loadNotifications();
    }
  }, [sessionReady, session?.user?.id]);

  // P07 (PV2-07, PV2-14): Realtime channel — new and updated notifications
  // are pushed in without requiring a manual refresh or focus re-trigger.
  // Scoped to this user's rows via the filter param so other users' inserts
  // are ignored by the server-side filter before delivery.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;

    const channel = supabase
      .channel(`user:${userId}:notifications`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => { loadNotifications(true, true); }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => { loadNotifications(true, false); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [session?.user?.id]);

  // Show only database notifications (single source of truth)
  const allNotifications = useMemo(() => {
    return dbNotifications.sort((a, b) => {
      const dateA = new Date(a.notification.sent_at).getTime();
      const dateB = new Date(b.notification.sent_at).getTime();
      return dateB - dateA;
    });
  }, [dbNotifications]);

  const groupedNotifications = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);

    const groups: { [key: string]: UserNotificationStatus[] } = {
      [t('notifications.groups.today')]: [],
      [t('notifications.groups.yesterday')]: [],
      [t('notifications.groups.thisWeek')]: [],
      [t('notifications.groups.older')]: []
    };

    allNotifications.forEach(notification => {
      const notificationDate = new Date(notification.notification.sent_at);
      const notificationDay = new Date(notificationDate.getFullYear(), notificationDate.getMonth(), notificationDate.getDate());

      if (notificationDay.getTime() === today.getTime()) {
        groups[t('notifications.groups.today')].push(notification);
      } else if (notificationDay.getTime() === yesterday.getTime()) {
        groups[t('notifications.groups.yesterday')].push(notification);
      } else if (notificationDate >= thisWeek) {
        groups[t('notifications.groups.thisWeek')].push(notification);
      } else {
        groups[t('notifications.groups.older')].push(notification);
      }
    });

    // Filter out empty groups
    return Object.entries(groups).filter(([_, notifications]) => notifications.length > 0);
  }, [allNotifications]);

  const handleClose = () => {
    router.dismiss();
  };

  const handleNotificationPress = async (notification: UserNotificationStatus) => {
    if (!notification.is_read) {
      try {
        const [{ NotificationsService }, { default: NotificationCacheService }, { default: BadgeManagerService }] = await Promise.all([
          import('../services/notificationsService'),
          import('../services/notificationCacheService'),
          import('../services/badgeManagerService')
        ]);

        if (session?.user && session?.access_token) {
          await Promise.all([
            NotificationsService.markAsRead(notification.notification_id, session.access_token),
            NotificationCacheService.markAsReadInCache(session.user.id, notification.notification_id)
          ]);
        }

        await BadgeManagerService.decrementBadge();
        console.log('📱 Badge decremented after tapping in-app notification');

        setDbNotifications(prev =>
          prev.map(n =>
            n.notification_id === notification.notification_id
              ? { ...n, is_read: true, read_at: new Date().toISOString() }
              : n
          )
        );

        if (notificationContext?.refreshNotifications) {
          notificationContext.refreshNotifications();
        }
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    const notificationType = notification.notification.notification_type;
    const linkUrl = notification.notification.link_url;

    if (notificationType === 'meeting_reminder' && linkUrl) {
      // Handle both path format (padrinhoelo://meeting/xxx) and query format (padrinho://meeting?meeting_id=xxx)
      const pathMatch = linkUrl.match(/\/meeting\/([^/?]+)/);
      const queryMatch = linkUrl.match(/meeting_id=([^&]+)/);
      const meetingId = pathMatch?.[1] || queryMatch?.[1];
      
      if (meetingId) {
        console.log('📱 Navigating to meeting detail page:', meetingId);
        router.push(`/meeting/${meetingId}`);
        return;
      }
    }

    // Handle appointment reminders — Phase 1: deep-link straight into the
    // video lobby for that appointment. The WhatsApp branch was removed
    // when sponsor↔patient calls moved fully in-app; the helper below
    // mints a fresh 3h join token, runs the permissions pre-check, and
    // routes to /video-call/lobby (or /permissions on first run).
    // The deep link emitted by the server is now
    // `padrinhoelo://video-call/lobby?appointmentId=...`, but we still
    // accept the legacy `appointment_id=` and `appointments/:id` patterns
    // so older queued notifications keep working.
    if (notificationType === 'appointment_reminder') {
      const appointmentIdMatch =
        linkUrl?.match(/appointmentId=([^&]+)/) ||
        linkUrl?.match(/appointment_id=([^&]+)/) ||
        linkUrl?.match(/appointments\/([^/?]+)/);
      const appointmentId = appointmentIdMatch?.[1];

      if (appointmentId) {
        const { joinAppointmentFromNotification } = await import('../services/joinAppointmentFromNotification');
        await joinAppointmentFromNotification(appointmentId);
        return;
      }

      console.log('📱 Could not extract appointmentId, navigating to /perfil');
      router.push('/perfil');
      return;
    }

    if (notificationType === 'community_support' || notificationType === 'community_encouragement') {
      if (linkUrl) {
        const communityPostIdMatch = linkUrl.match(/[?&]id=([^&]+)/);
        const communityPostId = communityPostIdMatch?.[1];
        if (communityPostId) {
          console.log('📱 Navigating to community post:', communityPostId);
          router.push({
            pathname: '/community-post',
            params: { id: communityPostId },
          } as any);
          return;
        }
      }
    }

    if (notificationType === 'community_follow') {
      if (linkUrl) {
        const profileIdMatch = linkUrl.match(/[?&]id=([^&]+)/);
        const profileId = profileIdMatch?.[1];
        if (profileId) {
          console.log('📱 Navigating to community profile:', profileId);
          router.push({
            pathname: '/community-profile',
            params: { userId: profileId },
          } as any);
          return;
        }
      }
    }

    if (notificationType === 'daily_checkin') {
      console.log('📱 Navigating to sentiment diary (daily check-in)');
      router.push('/diario-sentimento' as any);
      return;
    }

    if (notificationType === 'sobriety_milestone') {
      console.log('📱 Navigating to sobriety clock (milestone)');
      router.push('/sobriety-clock' as any);
      return;
    }

    if (notificationType === 'inactivity_nudge') {
      console.log('📱 Navigating to Bill chat (inactivity nudge)');
      router.push('/' as any);
      return;
    }

    if (linkUrl) {
      try {
        if (linkUrl.startsWith('/')) {
          router.push(linkUrl as any);
        } else if (linkUrl.startsWith('http')) {
          await Linking.openURL(linkUrl);
        }
      } catch (error) {
        console.error('Error opening link:', error);
      }
    }
  };

  const renderNotificationIcon = (iconType: string, backgroundColor: string) => {
    const iconColor = backgroundColor === '#3D3C38' ? "#DAD8CF" : "#151515";

    switch (iconType) {
      case 'heart':
        return (
          <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <Path d="M12.0017 5.92507C11.3979 5.24903 10.6225 4.72728 9.76648 4.42682C6.4652 3.25658 3.38047 5.59684 3.03157 8.87727C2.5431 13.8208 7.83511 16.8611 11.1366 19.572C11.5082 19.8819 12.0512 19.967 12.4989 19.7815C12.7138 19.7 12.8866 19.5452 13.0616 19.4014C16.3914 16.6725 21.6522 13.5358 20.9334 8.55669C20.401 5.07868 16.7703 3.12597 13.6389 4.67763C13.0203 4.98409 12.4606 5.40932 12.0017 5.92507Z" fill="white" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
        );
      case 'award':
        return (
          <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <Path d="M7.54688 13.54L7.56536 20.1728C7.56633 20.8159 8.26785 21.2109 8.81952 20.8801L11.5837 19.2222C11.8455 19.0645 12.1734 19.0645 12.4361 19.2222L15.1954 20.8782C15.7481 21.209 16.4496 20.812 16.4496 20.1679V13.5312" fill="white"/>
            <Path d="M7.54688 13.54L7.56536 20.1728C7.56633 20.8159 8.26785 21.2109 8.81952 20.8801L11.5837 19.2222C11.8455 19.0645 12.1734 19.0645 12.4361 19.2222L15.1954 20.8782C15.7481 21.209 16.4496 20.812 16.4496 20.1679V13.5312" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <Path fillRule="evenodd" clipRule="evenodd" d="M11.9995 3C15.4331 3 18.2158 5.78367 18.2158 9.21729C18.2158 12.6499 15.4331 15.4336 11.9995 15.4336C8.5659 15.4336 5.7832 12.6499 5.7832 9.21729C5.7832 5.78367 8.5659 3 11.9995 3Z" fill="white" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
        );
      case 'star':
        return (
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path d="M10 2.31094V17.111" stroke={iconColor} strokeWidth="1.5" strokeLinecap="square" />
            <Path d="M17.4 9.71095H2.6" stroke={iconColor} strokeWidth="1.5" strokeLinecap="square" />
            <Path d="M15.233 4.47852L4.768 14.9437" stroke={iconColor} strokeWidth="1.5" strokeLinecap="square" />
            <Path d="M15.233 14.9434L4.768 4.47818" stroke={iconColor} strokeWidth="1.5" strokeLinecap="square" />
          </Svg>
        );
      case 'flag':
        return (
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path d="M7.477 17.4205L4.795 4.27162L13.923 2.5L12.757 6.33919L15.259 9.38783L6.253 11.2924M3.612 17.5H16.788" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      case 'stars':
        return (
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path d="M9.09512 3.60833C9.45421 2.83884 10.5484 2.83884 10.9075 3.60832L11.7302 5.37119C12.3262 6.64837 13.3528 7.67497 14.6299 8.27098L16.3928 9.09365C17.1623 9.45275 17.1623 10.5469 16.3928 10.906L14.6299 11.7287C13.3528 12.3247 12.3262 13.3513 11.7302 14.6285L10.9075 16.3913C10.5484 17.1608 9.45421 17.1608 9.09512 16.3913L8.27245 14.6285C7.67643 13.3513 6.64983 12.3247 5.37266 11.7287L3.60979 10.906C2.84031 10.5469 2.8403 9.45275 3.60979 9.09365L5.37266 8.27098C6.64983 7.67497 7.67643 6.64837 8.27245 5.37119L9.09512 3.60833Z" fill="white" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
        );
      case 'bookmark':
        return (
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path fillRule="evenodd" clipRule="evenodd" d="M16.222 5.48865C16.222 3.35298 14.764 2.5 12.664 2.5H7.733C5.698 2.5 4.173 3.29703 4.173 5.34595V16.7638C4.172 17.17 4.501 17.5 4.908 17.5C5.035 17.5 5.159 17.4676 5.269 17.4059L10.221 14.6297L15.127 17.4011C15.482 17.6005 15.931 17.4757 16.131 17.1213C16.193 17.0111 16.226 16.887 16.226 16.7597L16.222 5.48865Z" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      case 'calendar':
        return (
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path d="M12.6289 3.07715V5.55658" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M6.60938 3.07715V5.55658" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path fillRule="evenodd" clipRule="evenodd" d="M13.0679 4.22021L6.17256 4.2209C4.14713 4.2209 2.88281 5.34879 2.88281 7.42193V13.663C2.88281 15.7689 4.14713 16.9229 6.17256 16.9229L13.062 16.9222C15.0933 16.9222 16.3516 15.7883 16.3516 13.7145V7.42125C16.3576 5.34811 15.0993 4.22021 13.0679 4.22021Z" fill="white" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M6.26953 9.53125H6.27786" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M6.26953 12.4097H6.27786" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M9.61328 9.53125H9.62161" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M9.61328 12.4097H9.62161" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M12.9492 9.53125H12.9576" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M12.9492 12.4097H12.9576" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      case 'megaphone':
        return (
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path d="M15.55 11.7593C15.55 11.7593 7.877 12.8888 6.914 12.9893C5.95 13.0899 4.425 12.3342 3.883 10.8431C3.34 9.352 4.022 7.79282 4.825 7.25118C5.628 6.70875 12.2 2.66113 12.2 2.66113" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M7.114 12.9621L8.344 16.3416C8.677 17.2546 9.685 17.7257 10.598 17.3925C11.511 17.0609 11.981 16.0514 11.65 15.1392L10.691 12.5065" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path fillRule="evenodd" clipRule="evenodd" d="M16.122 6.38782C17.042 8.9135 16.868 11.2965 15.732 11.7092C14.597 12.1227 12.932 10.4095 12.012 7.88379C11.093 5.35727 11.268 2.97508 12.402 2.56157C13.538 2.14805 15.203 3.86131 16.122 6.38782Z" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      case 'meeting':
        return (
          <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <Path d="M18.4222 14.1112C18.4222 14.1112 9.21383 15.4666 8.05792 15.5872C6.90202 15.7079 5.07183 14.8011 4.42091 13.0117C3.76998 11.2224 4.58826 9.35138 5.55151 8.70142C6.51477 8.0505 14.4018 3.19336 14.4018 3.19336" fill={iconColor} />
            <Path d="M18.4222 14.1112C18.4222 14.1112 9.21383 15.4666 8.05792 15.5872C6.90202 15.7079 5.07183 14.8011 4.42091 13.0117C3.76998 11.2224 4.58826 9.35138 5.55151 8.70142C6.51477 8.0505 14.4018 3.19336 14.4018 3.19336" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M8.29688 15.5546L9.77289 19.61C10.1718 20.7056 11.3822 21.2709 12.4778 20.871C13.5734 20.4731 14.1377 19.2617 13.7397 18.1671L12.5897 15.0078" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <Path fillRule="evenodd" clipRule="evenodd" d="M19.1084 7.66539C20.2118 10.6962 20.0026 13.5558 18.6404 14.0511C17.2782 14.5473 15.2797 12.4914 14.1763 9.46055C13.073 6.42873 13.2832 3.5701 14.6444 3.07388C16.0065 2.57766 18.005 4.63357 19.1084 7.66539Z" fill="white" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      case 'video':
        // Phase 1 — appointment reminders. Same camera-record glyph used on
        // the lobby, fatal screen, and appointment cards in /perfil.
        return <RecordVideoGlyph color={iconColor} size={20} />;
      case 'bell':
      default:
        return (
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path 
              fillRule="evenodd" 
              clipRule="evenodd" 
              d="M10.0013 14.8729C14.7007 14.8729 16.8747 14.27 17.0846 11.8503C17.0846 9.43217 15.5689 9.58766 15.5689 6.62076C15.5689 4.30329 13.3723 1.6665 10.0013 1.6665C6.63028 1.6665 4.43368 4.30329 4.43368 6.62076C4.43368 9.58766 2.91797 9.43217 2.91797 11.8503C3.12876 14.2792 5.30278 14.8729 10.0013 14.8729Z" 
              fill="white" 
              stroke="#151515" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <Path 
              d="M11.9914 17.3809C10.8546 18.6431 9.08125 18.6581 7.93359 17.3809" 
              stroke="#151515" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </Svg>
        );
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return t('common.now');
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h`;
    } else if (diffInDays === 1) {
      return t('common.yesterday');
    } else if (diffInDays < 7) {
      return `${diffInDays}d`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return weeks === 1 ? '1 semana' : `${weeks} semanas`;
    } else {
      const months = Math.floor(diffInDays / 30);
      return months === 1 ? '1 mês' : `${months} meses`;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleClose} style={styles.backButton}>
          <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
            <Path d="M16.0859 19L9.08594 12L16.0859 5" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
          </Svg>
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>{t('notifications.title')}</Text>
        </View>
        {isAdmin ? (
          <TouchableOpacity
            onPress={() => router.push('/notification-settings')}
            style={styles.settingsButton}
            accessibilityRole="button"
            accessibilityLabel={t('notifications.settings')}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
          </TouchableOpacity>
        ) : (
          <View style={styles.headerSpacer} />
        )}
      </View>

      {/* Notifications List */}
      <ScrollView style={styles.notificationsContainer} showsVerticalScrollIndicator={false}>
        {loading ? (
          <>
            <SkeletonNotification />
            <SkeletonNotification />
            <SkeletonNotification />
            <SkeletonNotification />
            <SkeletonNotification />
            <SkeletonNotification />
          </>
        ) : groupedNotifications.length > 0 ? (
        groupedNotifications.map(([groupTitle, notifications]) => (
          <View key={groupTitle} style={styles.notificationGroup}>
            <Text style={styles.groupTitle}>{groupTitle}</Text>
            {notifications.map((notificationStatus) => (
              <TouchableOpacity
                key={notificationStatus.notification_id}
                style={[
                  styles.notification,
                  notificationStatus.notification.link_url && styles.notificationClickable,
                  !notificationStatus.is_read && styles.notificationUnread
                ]}
                onPress={() => handleNotificationPress(notificationStatus)}
                activeOpacity={notificationStatus.notification.link_url ? 0.7 : 1}
              >
                <View style={styles.notificationContainer}>
                  {notificationStatus.notification.banner_url && (
                    <View style={styles.notificationBanner}>
                      <Image
                        source={{ uri: notificationStatus.notification.banner_url }}
                        style={styles.bannerImage}
                        contentFit="cover"
                      />
                    </View>
                  )}

                  <View style={styles.notificationContent}>
                    <View style={[styles.notificationIcon, { backgroundColor: notificationStatus.notification.background_color || '#F0EEE4' }]}>
                      {renderNotificationIcon(notificationStatus.notification.icon, notificationStatus.notification.background_color || '#F0EEE4')}
                    </View>

                    <View style={styles.notificationTextContainer}>
                      <View style={styles.notificationTexts}>
                        <Text style={[
                          styles.notificationText,
                          !notificationStatus.is_read && styles.notificationTextUnread
                        ]}>
                          <Text style={styles.notificationTitle}>
                            {notificationStatus.notification.title}
                          </Text>
                          {notificationStatus.notification.subtitle && (
                            <Text style={styles.notificationSubtitle}>
                              {' '}{notificationStatus.notification.subtitle}
                            </Text>
                          )}
                          <Text style={styles.notificationMessage}>
                            {' '}{notificationStatus.notification.message}
                          </Text>
                        </Text>
                      </View>
                    </View>

                    <View style={styles.notificationMeta}>
                      <Text style={styles.notificationTime}>
                        {formatTime(new Date(notificationStatus.notification.sent_at))}
                      </Text>
                      {!notificationStatus.is_read && (
                        <View style={styles.unreadIndicator} />
                      )}
                    </View>
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        ))
      ) : (
          <View style={styles.emptyState}>
            <View style={styles.emptyStateContent}>
              <View style={styles.emptyStateImageContainer}>
                <CachedImage
                  source={require('../assets/images/notification.png')}
                  style={styles.emptyStateImage}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.emptyStateTitle}>{t('notifications.empty.title')}</Text>
            </View>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  backButton: {
    padding: 4,
  },
  headerTitleContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    textAlign: 'center',
  },
  headerSpacer: {
    width: 32,
  },
  settingsButton: {
    padding: 4,
  },
  notificationsContainer: {
    flex: 1,
  },
  notificationGroup: {
    marginBottom: 0,
    paddingTop: 12,
    paddingBottom: 12,
  },
  groupTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    marginBottom: 12,
    marginHorizontal: 24,
  },
  notification: {
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 8,
    marginBottom: 0,
  },
  notificationClickable: {
    opacity: 1,
  },
  notificationContainer: {
    flexDirection: 'column',
    width: '100%',
    gap: 16,
  },
  notificationBanner: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#F9F8F3',
    borderWidth: 1,
    borderColor: '#F0EEE4',
  },
  bannerImage: {
    width: '100%',
    height: '100%',
  },
  notificationContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  notificationIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  notificationTextContainer: {
    flex: 1,
    marginRight: 8,
  },
  notificationTexts: {
    flexDirection: 'column',
  },
  notificationText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    color: '#151515',
    flexWrap: 'wrap',
  },
  notificationTextUnread: {
    fontFamily: 'Inter_600SemiBold',
  },
  notificationTitle: {
    fontWeight: 'bold',
    fontFamily: 'Inter_700Bold',
    color: '#151515',
  },
  notificationSubtitle: {
    fontWeight: '400',
    color: '#7E7D75',
  },
  notificationMessage: {
    fontWeight: '400',
    color: '#151515',
  },
  notificationTime: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    lineHeight: 12,
  },
  emptyState: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 80,
  },
  emptyStateContent: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
    flexDirection: 'column',
    width: '100%',
  },
  emptyStateImageContainer: {
    width: 280,
    height: 280,
  },
  emptyStateImage: {
    width: '100%',
    height: '100%',
  },
  emptyStateTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8, // 120% line height
  },
  notificationUnread: {
    backgroundColor: '#F9F8F3',
  },

  notificationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  unreadIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#007AFF',
  },
  loadingState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
    paddingHorizontal: 40,
  },
  loadingContent: {
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#7E7D75',
    textAlign: 'center',
  },
});